import { useState } from "react";
import { FORMATO_FECHA } from "../../../common/utils/constants";
import { getFechaHoyArgentina, parseARtoISO, isFechaFutura } from "../../../common/utils/functions/dateUtils";

export const useModalConfirm = ({ open, setOpen, selectedRows, onConfirm }) => {
    // Estados locales del formulario
    const [fechaContableInput, setFechaContableInput] = useState("");
    const [isFechaValida, setIsFechaValida] = useState(true);
    const [customHint, setCustomHint] = useState("");

    // Estado para rastrear el valor anterior de open
    // Inicializamos en false para que al montarse el componente con open=true,
    // la condición open && !prevOpen se evalúe como true
    const [prevOpen, setPrevOpen] = useState(false);

    // Inicializar el formulario con la fecha actual cuando se abre el modal
    // Ajustamos el estado inline durante el render para evitar render extra
    if (open && !prevOpen) {
        setPrevOpen(true);
        setFechaContableInput(getFechaHoyArgentina(FORMATO_FECHA.AR));
        setIsFechaValida(true);
        setCustomHint("");
    } else if (!open && prevOpen) {
        setPrevOpen(false);
    }


    const convertirFechaParaAPI = (fechaStr) => {
        if (!fechaStr || fechaStr.includes('-')) return fechaStr;

        return parseARtoISO(fechaStr);
    };

    // Función para verificar si el período contable está cerrado
    // La fecha ingresada debe ser POSTERIOR a todas las fechas de cierre
    const isPeriodoContableCerrado = (fechaStr) => {
        if (!fechaStr || !selectedRows || selectedRows.length === 0) return false;

        const fechaIngresadaISO = parseARtoISO(fechaStr);
        if (!fechaIngresadaISO) return false;

        // Verificar si hay alguna fila donde la fecha de cierre sea >= fecha ingresada
        return selectedRows.some(row => {
            const fechaCierreContable = row.ULT_CIERRE_CONTABLE;
            if (!fechaCierreContable) return false;

            const fechaCierreContableISO = parseARtoISO(fechaCierreContable);
            if (!fechaCierreContableISO) return false;

            // Si la fecha ingresada es menor o igual a la fecha de cierre, el período está cerrado
            return fechaIngresadaISO <= fechaCierreContableISO;
        });
    };

    // Handlers
    const handleFechaContableChange = (value) => {
        setFechaContableInput(value);

        // Si está vacío, mostrar hint de error
        if (!value || value.length === 0) {
            setCustomHint("La fecha contable es requerida");
            setIsFechaValida(false);
        } else if (isFechaFutura(value)) {
            setCustomHint("La fecha contable no puede ser futura");
            setIsFechaValida(false);
        } else if (isPeriodoContableCerrado(value)) {
            setCustomHint("La fecha seleccionada corresponde a un período contable cerrado para las cuentas seleccionadas");
            setIsFechaValida(false);
        } else {
            setCustomHint("");
        }
    };

    const handleValidateFechaContable = (isValid) => {
        // Este callback es llamado por InputDate al perder el foco
        // Guardamos el estado de validación para habilitar/deshabilitar el botón
        setIsFechaValida(isValid);

        // Si está vacío después de la validación, mostrar hint
        if (!fechaContableInput || fechaContableInput.length === 0) {
            setCustomHint("La fecha contable es requerida");
        } else if (isFechaFutura(fechaContableInput)) {
            setCustomHint("La fecha contable no puede ser futura");
            setIsFechaValida(false);
        } else if (isPeriodoContableCerrado(fechaContableInput)) {
            setCustomHint("La fecha seleccionada corresponde a un período contable cerrado para las cuentas seleccionadas");
            setIsFechaValida(false);
        } else {
            setCustomHint("");
        }
    };

    const handleAccept = () => {
        if (fechaContableInput.length === 10 && isFechaValida) {
            const fechaAPI = convertirFechaParaAPI(fechaContableInput);
            onConfirm(selectedRows, fechaAPI);
            setOpen(false);
        }
    };

    const handleCloseModal = () => {
        setFechaContableInput("");
        setIsFechaValida(true);
        setCustomHint("");
        setOpen(false);
    };

    // Validaciones
    // El botón está deshabilitado si la fecha está vacía o es inválida
    const isAcceptDisabled = !fechaContableInput ||
        fechaContableInput.length === 0 ||
        fechaContableInput.length !== 10 ||
        !isFechaValida;

    return {
        // Estados del formulario
        fechaContableInput,
        customHint,

        // Handlers
        handleAccept,
        handleFechaContableChange,
        handleValidateFechaContable,
        handleCloseModal,

        // Validaciones
        isAcceptDisabled,
    };
};
